import React, { useState, useEffect } from 'react';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { financeService } from '../services/financeService';
import { analyticsService } from '../services/analyticsService';
import { withCache, invalidateCache } from '../services/cacheService';
import { useSubmitLock } from '../hooks/useSubmitLock';
import AsyncSearchSelect from '../components/AsyncSearchSelect';
import AssetInstallmentsDialog from '../components/AssetInstallmentsDialog';
import InventoryAnalytics from '../components/InventoryAnalytics';
import FinanceAnalytics from '../components/FinanceAnalytics';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Stack,
  Skeleton,
  Avatar,
  Fade,
  useTheme,
  useMediaQuery,
  Badge,
  Tabs,
  Tab,
  TablePagination,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Timeline as TimelineIcon,
  Inventory as InventoryIcon,
  AttachMoney as MoneyIcon,
  History as HistoryIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  AddCircle as AddCircleIcon,
  List as ListIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ViewModule as ViewModuleIcon,
  Payments as PaymentsIcon
} from '@mui/icons-material';

import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';

// Categorias disponíveis para os ativos
const ASSET_CATEGORIES = [
  'Carros',
  'Motos', 
  'Caminhões',
  'Imóveis',
  'Acessórios',
  'Eletrônicos',
  'Dinheiro',
  'Materiais',
  'Móveis',
  'Consumíveis',
  'Ferramentas',
  'Peças',
  'Objetos'
];

// TabPanel component for tab content
const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`asset-tabpanel-${index}`}
      aria-labelledby={`asset-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const AssetManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showNotification } = useNotification();
  const { hasPermission } = useAuth();
  const [savingAsset, runSaveAsset] = useSubmitLock();
  const [savingCost, runSaveCost] = useSubmitLock();
  const [installmentsAsset, setInstallmentsAsset] = useState(null);
  const [payInInstallments, setPayInInstallments] = useState(false);
  const canManageInstallments = hasPermission('finance.transactions.update');
  
  // Estados principais
  const [assets, setAssets] = useState([]);
  const [assetsTotal, setAssetsTotal] = useState(0);
  const [assetSummary, setAssetSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [currentTab, setCurrentTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Estados dos modals
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [preparationCostsDialogOpen, setPreparationCostsDialogOpen] = useState(false);
  const [preparationCostDialog, setPreparationCostDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: '',
    assetId: null,
    title: '',
    message: ''
  });

  // Estados de dados
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetAttachments, setAssetAttachments] = useState([]);
  const [assetAttachmentsLoading, setAssetAttachmentsLoading] = useState(false);
  const [assetAttachmentsUploading, setAssetAttachmentsUploading] = useState(false);
  const [assetHistory, setAssetHistory] = useState([]);
  const [preparationCostsList, setPreparationCostsList] = useState([]);
  const [products, setProducts] = useState([]);
  const [enums, setEnums] = useState({
    assetStatuses: [],
    transactionClassifications: []
  });

  // Estados de formulários
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    brand: '',
    model: '',
    year: '',
    initial_value: '',
    preparation_costs: '0',
    location: '',
    notes: '',
    product_id: '',
    managed_by: '',
    bank_id: '',
    subclassification_id: '',
    transaction_date: new Date(),
    due_date: null,
    payment_date: null
  });

  const [selectedProductLabel, setSelectedProductLabel] = useState('');
  const [selectedBankLabel, setSelectedBankLabel] = useState('');
  const [selectedSubclassificationLabel, setSelectedSubclassificationLabel] = useState('');

  const [editRequestReason, setEditRequestReason] = useState('');

  const [preparationCostData, setPreparationCostData] = useState({
    amount: '',
    description: '',
    classification: 'custo_variavel',
    subclassification_id: '',
    bank_id: '',
    notes: '',
    transaction_date: new Date(),
    due_date: null,
    payment_date: null
  });

  const [preparationCostBankLabel, setPreparationCostBankLabel] = useState('');
  const [preparationCostSubclassificationLabel, setPreparationCostSubclassificationLabel] = useState('');

  // Status do pipeline de ativos
  const statusSteps = [
    { key: 'em_aquisicao', label: 'Em Aquisição' },
    { key: 'em_preparacao', label: 'Em Preparação' },
    { key: 'pronto_sorteio', label: 'Pronto para Sorteio' },
    { key: 'sorteado_aguardando', label: 'Sorteado (Aguardando)' },
    { key: 'entregue', label: 'Entregue' }
  ];

  const isSelectedAssetLocked = Boolean(selectedAsset?.acquisition_is_verified);
  const canReviewLockedAsset = hasPermission('finance.transactions.verify');
  const shouldCreateEditRequest = Boolean(selectedAsset && isSelectedAssetLocked && !canReviewLockedAsset);

  const canReadAssetAttachments = hasPermission('inventory.asset_attachments.read');
  const canCreateAssetAttachments = hasPermission('inventory.asset_attachments.create');
  const canDeleteAssetAttachments = hasPermission('inventory.asset_attachments.delete');

  const formatBytes = (bytes) => {
    if (bytes == null || Number.isNaN(Number(bytes))) return '-';
    const value = Number(bytes);
    if (value < 1024) return `${value} B`;
    const kb = value / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    const gb = mb / 1024;
    return `${gb.toFixed(1)} GB`;
  };

  const loadAssetAttachments = async (assetId) => {
    if (!assetId) return;
    if (!canReadAssetAttachments) {
      setAssetAttachments([]);
      return;
    }
    try {
      setAssetAttachmentsLoading(true);
      const res = await financeService.getAssetAttachments(assetId);
      setAssetAttachments(res?.data || []);
    } catch (err) {
      setAssetAttachments([]);
      showNotification(err.message || 'Erro ao carregar anexos', 'error');
    } finally {
      setAssetAttachmentsLoading(false);
    }
  };

  const handleUploadAssetFiles = async (event) => {
    const assetId = selectedAsset?.id;
    if (!assetId) return;

    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;

    try {
      setAssetAttachmentsUploading(true);
      for (const file of files) {
        const presign = await financeService.presignAssetAttachment(assetId, {
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          size_bytes: file.size
        });

        await financeService.uploadToPresignedUrl(presign.upload_url, presign.headers || {}, file);
        await financeService.completeAssetAttachment(assetId, presign.id);
      }

      showNotification('Anexo(s) enviado(s) com sucesso', 'success');
      await loadAssetAttachments(assetId);
    } catch (err) {
      showNotification(err.message || 'Erro ao enviar anexo', 'error');
    } finally {
      setAssetAttachmentsUploading(false);
    }
  };

  const handleOpenAssetAttachment = async (attachmentId, mode) => {
    const assetId = selectedAsset?.id;
    if (!assetId || !attachmentId) return;
    try {
      const res = await financeService.getAssetAttachmentSignedUrl(assetId, attachmentId, mode);
      if (res?.url) {
        window.open(res.url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      showNotification(err.message || 'Erro ao abrir anexo', 'error');
    }
  };

  const handleDeleteAssetAttachment = async (attachmentId) => {
    const assetId = selectedAsset?.id;
    if (!assetId || !attachmentId) return;
    if (!window.confirm('Remover este anexo?')) return;
    try {
      await financeService.deleteAssetAttachment(assetId, attachmentId);
      showNotification('Anexo removido', 'success');
      await loadAssetAttachments(assetId);
    } catch (err) {
      showNotification(err.message || 'Erro ao remover anexo', 'error');
    }
  };

  useEffect(() => {
    loadData();
    loadEnums();
    loadProducts();
  }, []);

  useEffect(() => {
    if (!loading) {
      loadAssetsPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, statusFilter, categoryFilter, searchTerm]);

  const loadProducts = async () => {
    try {
      // Cache de 10 minutos para produtos
      const productsData = await withCache(
        'asset_products_list',
        () => financeService.getAvailableProducts(),
        10 * 60 * 1000
      );
      setProducts(productsData);
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
    }
  };

  const normalizeId = (value) => {
    const str = String(value ?? '').trim();
    return str ? str : null;
  };

  // If dialog opens before products preload finishes, backfill the product label
  useEffect(() => {
    if (!dialogOpen) return;
    if (!formData.product_id) return;
    if (selectedProductLabel) return;

    const targetId = String(formData.product_id).trim();
    const match = products.find(p => String(p.id).trim() === targetId);
    if (match?.title) setSelectedProductLabel(match.title);
  }, [dialogOpen, formData.product_id, products, selectedProductLabel]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [summaryData] = await Promise.all([
        withCache(
          'asset_summary_dashboard',
          () => financeService.getAssetSummary(),
          5 * 60 * 1000
        )
      ]);
      await loadAssetsPage(true);
      setAssetSummary(summaryData);
    } catch (err) {
      setError(err.message);
      showNotification('Erro ao carregar dados', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAssetsPage = async (reset = false) => {
    try {
      const params = {
        q: searchTerm || '',
        status: statusFilter === 'all' ? '' : statusFilter,
        category: categoryFilter === 'all' ? '' : categoryFilter,
        offset: (reset ? 0 : page * rowsPerPage),
        limit: rowsPerPage
      };
      const resp = await financeService.getAssetsPage(params);
      const data = resp?.data || [];
      setAssets(data);
      setAssetsTotal(resp?.total || data.length || 0);
      if (reset) setPage(0);
    } catch (err) {
      console.error('Erro ao carregar ativos paginados:', err);
      setAssets([]);
      setAssetsTotal(0);
    }
  };

  const loadEnums = async () => {
    try {
      // Cache de 1 hora para enums usados em formulários
      const [statuses, classifications] = await Promise.all([
        withCache(
          'asset_statuses_list',
          () => financeService.getAssetStatuses(),
          60 * 60 * 1000
        ),
        withCache(
          'financial_enums_classifications',
          () => financeService.getTransactionClassifications(),
          60 * 60 * 1000
        )
      ]);
      setEnums({
        assetStatuses: statuses,
        transactionClassifications: classifications || []
      });
    } catch (err) {
      console.error('Erro ao carregar enums:', err);
    }
  };

  const handleShowPreparationCosts = async (asset) => {
    try {
      const costs = await financeService.getAssetPreparationCosts(asset.id);
      setPreparationCostsList(costs);
      setPreparationCostsDialogOpen(true);
    } catch (err) {
      showNotification('Erro ao buscar custos de preparação', 'error');
    }
  };

  const handleOpenDialog = (asset = null) => {
    setSelectedAsset(asset);
    setEditRequestReason('');
    if (asset) {
      const normalizedProductId = asset.product_id != null ? String(asset.product_id).trim() : '';
      const match = normalizedProductId ? products.find(p => String(p.id).trim() === normalizedProductId) : null;
      setSelectedProductLabel(match?.title || '');
      setSelectedBankLabel(asset.acquisition_bank_name || '');
      setSelectedSubclassificationLabel(asset.acquisition_subclassification_name || '');
      setFormData({
        name: asset.name,
        description: asset.description || '',
        category: asset.category,
        brand: asset.brand || '',
        model: asset.model || '',
        year: asset.year || '',
        initial_value: asset.initial_value.toString(),
        preparation_costs: asset.preparation_costs?.toString() || '0',
        location: asset.location || '',
        notes: asset.notes || '',
        product_id: normalizedProductId,
        managed_by: asset.managed_by || '',
        bank_id: asset.acquisition_bank_id || '',
        subclassification_id: asset.acquisition_subclassification_id || '',
        transaction_date: new Date(),
        due_date: null,
        payment_date: null
      });

      setAssetAttachments([]);
      loadAssetAttachments(asset.id);
    } else {
      setSelectedProductLabel('');
      setSelectedBankLabel('');
      setSelectedSubclassificationLabel('');
      setAssetAttachments([]);
      setFormData({
        name: '',
        description: '',
        category: '',
        brand: '',
        model: '',
        year: '',
        initial_value: '',
        preparation_costs: '0',
        location: '',
        notes: '',
        product_id: '',
        managed_by: '',
        bank_id: '',
        subclassification_id: '',
        transaction_date: new Date(),
        due_date: null,
        payment_date: null
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setPayInInstallments(false);
    setSelectedAsset(null);
    setAssetAttachments([]);
    setAssetAttachmentsLoading(false);
    setAssetAttachmentsUploading(false);
    setSelectedProductLabel('');
    setSelectedBankLabel('');
    setSelectedSubclassificationLabel('');
    setEditRequestReason('');
    setFormData({
      name: '',
      description: '',
      category: '',
      brand: '',
      model: '',
      year: '',
      initial_value: '',
      preparation_costs: '0',
      location: '',
      notes: '',
      product_id: '',
      managed_by: '',
      bank_id: '',
      subclassification_id: '',
      transaction_date: new Date(),
      due_date: null,
      payment_date: null
    });
  };

  const handleSaveAsset = async () => {
    try {
      if (selectedAsset) {
        const isLocked = Boolean(selectedAsset.acquisition_is_verified);
        const canReview = hasPermission('finance.transactions.verify');
        const shouldCreateRequest = isLocked && !canReview;

        const normalizedBankId = formData.bank_id ? String(formData.bank_id).trim() : null;
        const normalizedSubclassificationId = formData.subclassification_id ? String(formData.subclassification_id).trim() : null;
        // Edição: bloquear mudanças de valores (aquisição e custos adicionais)
        const assetUpdateData = {
          name: formData.name,
          category: formData.category,
          managed_by: formData.managed_by || null,
          product_id: normalizeId(formData.product_id),
          bank_id: normalizedBankId,
          subclassification_id: normalizedSubclassificationId,
          description: formData.description?.trim() || null,
          brand: formData.brand?.trim() || null,
          model: formData.model?.trim() || null,
          year: formData.year?.trim() || null,
          location: formData.location?.trim() || null,
          notes: formData.notes?.trim() || null
        };

        if (shouldCreateRequest) {
          const requestPayload = {
            reason: editRequestReason?.trim() || null,
            name: assetUpdateData.name,
            description: assetUpdateData.description,
            category: assetUpdateData.category,
            brand: assetUpdateData.brand,
            model: assetUpdateData.model,
            year: assetUpdateData.year,
            location: assetUpdateData.location,
            notes: assetUpdateData.notes,
            product_id: assetUpdateData.product_id,
            bank_id: assetUpdateData.bank_id,
            subclassification_id: assetUpdateData.subclassification_id
          };
          await financeService.createAssetEditRequest(selectedAsset.id, requestPayload);
          showNotification('Solicitação enviada para aprovação', 'success');
        } else {
          await financeService.updateAsset(selectedAsset.id, assetUpdateData);
          showNotification('Ativo atualizado com sucesso', 'success');
        }
      } else {
        const normalizedBankId = formData.bank_id ? String(formData.bank_id).trim() : null;
        const normalizedSubclassificationId = formData.subclassification_id ? String(formData.subclassification_id).trim() : null;
        const assetCreateData = {
          name: formData.name,
          category: formData.category,
          initial_value: parseFloat(formData.initial_value),
          managed_by: formData.managed_by || null,
          product_id: normalizeId(formData.product_id),
          bank_id: normalizedBankId,
          subclassification_id: normalizedSubclassificationId,
          description: formData.description?.trim() || null,
          brand: formData.brand?.trim() || null,
          model: formData.model?.trim() || null,
          year: formData.year?.trim() || null,
          location: formData.location?.trim() || null,
          notes: formData.notes?.trim() || null,
          transaction_date: formData.transaction_date || null,
          due_date: formData.due_date || null,
          payment_date: formData.payment_date || null
        };
        const createdAsset = await financeService.createAsset(assetCreateData);
        showNotification('Ativo criado com sucesso', 'success');
        if (payInInstallments) setInstallmentsAsset(createdAsset);
      }
      
      handleCloseDialog();
      loadData();
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleAdvanceStatus = (assetId) => {
    setConfirmDialog({
      open: true,
      action: 'advance',
      assetId: assetId,
      title: 'Avançar Status',
      message: 'Tem certeza que deseja avançar o status deste ativo?'
    });
  };

  const handleRevertStatus = (assetId) => {
    setConfirmDialog({
      open: true,
      action: 'revert',
      assetId: assetId,
      title: 'Retroceder Status',
      message: 'Tem certeza que deseja retroceder o status deste ativo?'
    });
  };

  const confirmStatusAction = async () => {
    try {
      const { action, assetId } = confirmDialog;
      const actionData = {
        reason: action === 'advance' ? 'Status avançado pelo usuário' : 'Status revertido pelo usuário'
      };

      if (action === 'advance') {
        await financeService.advanceAssetStatus(assetId, actionData);
        showNotification('Status avançado com sucesso', 'success');
      } else {
        await financeService.revertAssetStatus(assetId, actionData);
        showNotification('Status revertido com sucesso', 'success');
      }
      
      loadData();
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setConfirmDialog({ ...confirmDialog, open: false });
    }
  };

  const handleOpenPreparationCostDialog = (asset) => {
    setSelectedAsset(asset);

    const prefillBankId = asset?.acquisition_bank_id ? String(asset.acquisition_bank_id).trim() : '';
    const prefillBankLabel = asset?.acquisition_bank_name ? String(asset.acquisition_bank_name) : '';

    setPreparationCostData({
      amount: '',
      description: '',
      classification: 'custo_variavel',
      subclassification_id: '',
      bank_id: prefillBankId,
      notes: '',
      transaction_date: new Date(),
      due_date: null,
      payment_date: null
    });
    setPreparationCostBankLabel(prefillBankLabel);
    setPreparationCostSubclassificationLabel('');
    setPreparationCostDialog(true);
  };

  const handleAddPreparationCost = async () => {
    try {
      const normalizedBankId = String(preparationCostData.bank_id || '').trim();
      const normalizedClassification = String(preparationCostData.classification || '').trim();
      const normalizedSubclassificationId = String(preparationCostData.subclassification_id || '').trim();
      const costData = {
        amount: parseFloat(preparationCostData.amount),
        description: preparationCostData.description,
        classification: normalizedClassification || 'custo_variavel',
        subclassification_id: normalizedSubclassificationId || null,
        bank_id: normalizedBankId ? normalizedBankId : null,
        notes: preparationCostData.notes?.trim() || null,
        transaction_date: preparationCostData.transaction_date || null,
        due_date: preparationCostData.due_date || null,
        payment_date: preparationCostData.payment_date || null
      };

      await financeService.addPreparationCost(selectedAsset.id, costData);
      showNotification('Custo de preparação adicionado com sucesso', 'success');
      
      setPreparationCostDialog(false);
      setSelectedAsset(null);
      setPreparationCostBankLabel('');
      loadData();
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (window.confirm('Tem certeza que deseja deletar este ativo?')) {
      try {
        await financeService.deleteAsset(assetId);
        showNotification('Ativo deletado com sucesso', 'success');
        loadData();
      } catch (err) {
        if(err.message === "Request failed with status code 400") {
          showNotification('Não é possível deletar um ativo com uma transação vinculada. Contate o financeiro.', 'error');
        }
        showNotification(err.message, 'error');
      }
    }
  };

  const handleShowHistory = async (asset) => {
    try {
      const history = await financeService.getAssetStatusHistory(asset.id);
      setAssetHistory(history);
      setSelectedAsset(asset);
      setHistoryDialogOpen(true);
    } catch (err) {
      showNotification('Erro ao carregar histórico', 'error');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getStatusColor = (status) => {
    const colors = {
      'em_aquisicao': 'info',
      'em_preparacao': 'warning',
      'pronto_sorteio': 'success',
      'sorteado_aguardando': 'secondary',
      'entregue': 'primary',
      'cancelado': 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'em_aquisicao': 'Em Aquisição',
      'em_preparacao': 'Em Preparação',
      'pronto_sorteio': 'Pronto para Sorteio',
      'sorteado_aguardando': 'Sorteado (Aguardando)',
      'entregue': 'Entregue',
      'cancelado': 'Cancelado'
    };
    return labels[status] || status;
  };

  const getActiveStep = (status) => {
    return statusSteps.findIndex(step => step.key === status);
  };

  const getAssetSummary = () => {
    // Prefer backend summary (exclui ENTREGUE + históricos), fallback para cálculo local simples
    if (assetSummary) {
      return {
        total_count: assetSummary.total_assets || 0,
        total_value: assetSummary.total_value || 0,
        total_initial_value: assetSummary.total_initial_investment_current || 0,
        total_preparation_costs: assetSummary.total_preparation_costs || 0,
        // Expor históricos para uso nos cards
        total_value_historical: assetSummary.total_value_historical || 0,
        total_initial_value_historical: assetSummary.total_initial_investment_historical || 0,
        total_preparation_costs_historical: assetSummary.total_preparation_costs_historical || 0,
      };
    }

    // Fallback local (inclui todos os ativos)
    return assets.reduce((acc, asset) => {
      acc.total_count++;
      acc.total_value += parseFloat(asset.final_value || 0);
      acc.total_initial_value += parseFloat(asset.initial_value || 0);
      acc.total_preparation_costs += parseFloat(asset.preparation_costs || 0);
      return acc;
    }, { total_count: 0, total_value: 0, total_initial_value: 0, total_preparation_costs: 0 });
  };

  // Filtrar assets baseado na pesquisa, status e categoria
  const filteredAssets = assets; // server-side filtered

  // Pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Paginated data for display (only for table view)
  const paginatedAssets = filteredAssets;

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width={300} height={48} />
          <Skeleton variant="text" width={200} height={32} />
        </Box>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[...Array(4)].map((_, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert 
          severity="error" 
          sx={{ borderRadius: 2, mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={loadData}>
              Tentar Novamente
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  const summary = getAssetSummary();

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems={isMobile ? "flex-start" : "center"} 
          flexDirection={isMobile ? "column" : "row"}
          gap={2}
          mb={3}
        >
          <Box>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1" 
              fontWeight="700"
              sx={{ 
                background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              Controle de Estoque de Prêmios
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gerencie seus ativos e acompanhe o status em tempo real
            </Typography>
          </Box>
          
          <Stack direction={isMobile ? "column" : "row"} spacing={1} sx={{ width: isMobile ? "100%" : "auto" }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              size="small"
            >
              Atualizar
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              size="small"
              sx={{ 
                background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                boxShadow: '0 3px 10px rgba(25, 118, 210, 0.3)'
              }}
            >
              Novo Ativo
            </Button>
          </Stack>
        </Box>

        {/* Filtros e Pesquisa */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            mb: 3, 
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.9), rgba(248,250,252,0.9))'
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Pesquisar ativos..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                  sx: { borderRadius: 2 }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Status"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">Todos os Status</MenuItem>
                  {statusSteps.map(step => (
                    <MenuItem key={step.key} value={step.key}>
                      {step.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Categoria</InputLabel>
                <Select
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Categoria"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">Todas as Categorias</MenuItem>
                  {ASSET_CATEGORIES.map(category => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="text.secondary">
                  Encontrados: {assetsTotal}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant={viewMode === 'table' ? 'contained' : 'outlined'}
                  startIcon={<ViewModuleIcon />}
                  onClick={() => setViewMode(viewMode === 'table' ? 'cards' : 'table')}
                  size="small"
                  sx={{ borderRadius: 2 }}
                >
                  {viewMode === 'table' ? 'Cards' : 'Tabela'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Cards de Resumo Modernos */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Card 
            elevation={0}
            sx={{ 
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(135deg, #f8fafc, #ffffff)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                borderColor: 'primary.main'
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                    Total de Ativos
                  </Typography>
                  <Typography variant="h4" fontWeight="700" color="primary.main">
                    {summary.total_count}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    <TrendingUpIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    Inventário ativo
                  </Typography>
                </Box>
                <Avatar sx={{ 
                  bgcolor: 'primary.main', 
                  width: 56, 
                  height: 56,
                  background: 'linear-gradient(45deg, #1976d2, #42a5f5)'
                }}>
                  <InventoryIcon sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <Card 
            elevation={0}
            sx={{ 
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(135deg, #f0fdf4, #ffffff)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                borderColor: 'success.main'
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                    Valor Total
                  </Typography>
                  <Typography variant="h4" fontWeight="700" color="success.main">
                    {formatCurrency(summary.total_value)}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    <TrendingUpIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    Patrimônio atual (exclui entregues)
                  </Typography>
                  {summary.total_value_historical !== undefined && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Histórico: {formatCurrency(summary.total_value_historical)}
                    </Typography>
                  )}
                </Box>
                <Avatar sx={{ 
                  bgcolor: 'success.main', 
                  width: 56, 
                  height: 56,
                  background: 'linear-gradient(45deg, #2e7d32, #4caf50)'
                }}>
                  <MoneyIcon sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <Card 
            elevation={0}
            sx={{ 
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(135deg, #f0f9ff, #ffffff)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                borderColor: 'info.main'
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                    Investimento Inicial
                  </Typography>
                  <Typography variant="h4" fontWeight="700" color="info.main">
                    {formatCurrency(summary.total_initial_value)}
                  </Typography>
                  <Typography variant="caption" color="info.main">
                    <TrendingUpIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    Capital investido (inventário)
                  </Typography>
                  {summary.total_initial_value_historical !== undefined && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Histórico: {formatCurrency(summary.total_initial_value_historical)}
                    </Typography>
                  )}
                </Box>
                <Avatar sx={{ 
                  bgcolor: 'info.main', 
                  width: 56, 
                  height: 56,
                  background: 'linear-gradient(45deg, #0288d1, #29b6f6)'
                }}>
                  <MoneyIcon sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <Card 
            elevation={0}
            sx={{ 
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(135deg, #fefce8, #ffffff)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                borderColor: 'warning.main'
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                    Custos de Preparação
                  </Typography>
                  <Typography variant="h4" fontWeight="700" color="warning.main">
                    {formatCurrency(summary.total_preparation_costs)}
                  </Typography>
                  <Typography variant="caption" color="warning.main">
                    <TrendingUpIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    Custos de preparação (inventário)
                  </Typography>
                  {summary.total_preparation_costs_historical !== undefined && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Histórico: {formatCurrency(summary.total_preparation_costs_historical)}
                    </Typography>
                  )}
                </Box>
                <Avatar sx={{ 
                  bgcolor: 'warning.main', 
                  width: 56, 
                  height: 56,
                  background: 'linear-gradient(45deg, #ed6c02, #ff9800)'
                }}>
                  <MoneyIcon sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs Navigation */}
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={(e, newValue) => setCurrentTab(newValue)}
          aria-label="asset management tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 64,
            },
          }}
        >
          <Tab 
            label="Lista de Ativos" 
            icon={<ListIcon />} 
            iconPosition="start"
            sx={{ flexDirection: 'row', gap: 1 }}
          />
          {/* <Tab 
            label="Analytics de Inventário" 
            icon={<TrendingUpIcon />} 
            iconPosition="start"
            sx={{ flexDirection: 'row', gap: 1 }}
          />
          <Tab 
            label="Analytics Financeiros" 
            icon={<MoneyIcon />} 
            iconPosition="start"
            sx={{ flexDirection: 'row', gap: 1 }}
          /> */}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={currentTab} index={0}>
        {/* Lista de Ativos */}
      {viewMode === 'table' ? (
        <Paper 
          elevation={0} 
          sx={{ 
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden'
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Ativo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Categoria/Modelo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Valor Inicial</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Custos Prep.</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Valor Final</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Localização</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedAssets.map((asset, index) => (
                  <Fade in={true} timeout={300 + index * 100} key={asset.id}>
                    <TableRow 
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: 'grey.50',
                          transition: 'all 0.2s ease'
                        },
                        borderLeft: '4px solid',
                        borderLeftColor: `${getStatusColor(asset.status)}.main`
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {asset.name}
                          </Typography>
                          {asset.brand && asset.model && (
                            <Typography variant="caption" color="text.secondary">
                              {asset.brand} {asset.model} {asset.year}
                            </Typography>
                          )}
                          {asset.acquisition_bank_name && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Banco: {asset.acquisition_bank_name}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={asset.category} 
                          size="small" 
                          variant="outlined"
                          sx={{ borderRadius: 2 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Chip
                            label={getStatusLabel(asset.status)}
                            color={getStatusColor(asset.status)}
                            size="small"
                            sx={{ borderRadius: 2, fontWeight: 500 }}
                          />
                          {!isMobile && (
                            <Box sx={{ width: 200, mt: 1 }}>
                              <Stepper activeStep={getActiveStep(asset.status)} alternativeLabel>
                                {statusSteps.map((step) => (
                                  <Step key={step.key}>
                                    <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.7rem' } }}>
                                      {step.label}
                                    </StepLabel>
                                  </Step>
                                ))}
                              </Stepper>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="500">
                          {formatCurrency(asset.initial_value)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Badge
                          badgeContent={asset.preparation_costs > 0 && asset.unverified_preparation_costs > 0 ? "!" : null}
                          color="warning"
                          invisible={!(asset.preparation_costs > 0 && asset.unverified_preparation_costs > 0)}
                        >
                          <Typography 
                            variant="body2" 
                            fontWeight="500"
                            color={asset.preparation_costs > 0 ? 'warning.main' : 'text.secondary'}
                          >
                            {formatCurrency(asset.preparation_costs)}
                          </Typography>
                        </Badge>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="700" color="success.main">
                          {formatCurrency(asset.final_value)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {asset.location || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Editar">
                            <IconButton
                              onClick={() => handleOpenDialog(asset)}
                              size="small"
                              sx={{ '&:hover': { backgroundColor: 'primary.main', color: 'white' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Histórico">
                            <IconButton
                              onClick={() => handleShowHistory(asset)}
                              size="small"
                              sx={{ '&:hover': { backgroundColor: 'info.main', color: 'white' } }}
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {canManageInstallments && (
                            <Tooltip title="Parcelas da Aquisição: lançar a compra paga em mais de uma vez">
                              <IconButton
                                onClick={() => setInstallmentsAsset(asset)}
                                size="small"
                                sx={{ '&:hover': { backgroundColor: 'success.main', color: 'white' } }}
                              >
                                <PaymentsIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          <Tooltip title="Adicionar Custo">
                            <IconButton
                              onClick={() => handleOpenPreparationCostDialog(asset)}
                              size="small"
                              sx={{ '&:hover': { backgroundColor: 'warning.main', color: 'white' } }}
                            >
                              <AddCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Ver Custos">
                            <IconButton
                              onClick={() => handleShowPreparationCosts(asset)}
                              size="small"
                              sx={{ '&:hover': { backgroundColor: 'secondary.main', color: 'white' } }}
                            >
                              <ListIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Retroceder Status">
                            <IconButton
                              onClick={() => handleRevertStatus(asset.id)}
                              size="small"
                              disabled={asset.status === 'em_aquisicao'}
                              sx={{ '&:hover': { backgroundColor: 'error.main', color: 'white' } }}
                            >
                              <ArrowBackIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Avançar Status">
                            <IconButton
                              onClick={() => handleAdvanceStatus(asset.id)}
                              size="small"
                              disabled={asset.status === 'entregue' || asset.status === 'cancelado'}
                              sx={{ '&:hover': { backgroundColor: 'success.main', color: 'white' } }}
                            >
                              <ArrowForwardIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Deletar">
                            <IconButton
                              onClick={() => handleDeleteAsset(asset.id)}
                              size="small"
                              sx={{ '&:hover': { backgroundColor: 'error.main', color: 'white' } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </Fade>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
              <TablePagination
              component="div"
                count={assetsTotal}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
              }
              sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                '& .MuiTablePagination-toolbar': {
                  padding: '0 16px',
                }
              }}
            />
          </Box>
        </Paper>
      ) : (
        // Cards View
        <Grid container spacing={3}>
          {filteredAssets.map((asset, index) => (
            <Grid item xs={12} sm={6} lg={4} key={asset.id}>
              <Fade in={true} timeout={300 + index * 100}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderLeft: '4px solid',
                    borderLeftColor: `${getStatusColor(asset.status)}.main`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                      borderColor: `${getStatusColor(asset.status)}.main`
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box mb={2}>
                      <Typography variant="h6" fontWeight="600" gutterBottom>
                        {asset.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {asset.brand && asset.model ? `${asset.brand} ${asset.model} ${asset.year}` : asset.category}
                      </Typography>
                      {asset.acquisition_bank_name && (
                        <Typography variant="body2" color="text.secondary">
                          Banco: {asset.acquisition_bank_name}
                        </Typography>
                      )}
                    </Box>

                    <Box mb={2}>
                      <Chip
                        label={getStatusLabel(asset.status)}
                        color={getStatusColor(asset.status)}
                        size="small"
                        sx={{ borderRadius: 2, fontWeight: 500 }}
                      />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Valor Inicial
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                          {formatCurrency(asset.initial_value)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Custos Prep.
                        </Typography>
                        <Typography 
                          variant="body2" 
                          fontWeight="600"
                          color={asset.preparation_costs > 0 ? 'warning.main' : 'text.secondary'}
                        >
                          {formatCurrency(asset.preparation_costs)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Valor Final
                        </Typography>
                        <Typography variant="h6" fontWeight="700" color="success.main">
                          {formatCurrency(asset.final_value)}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Box mb={2}>
                      <Stepper activeStep={getActiveStep(asset.status)} alternativeLabel>
                        {statusSteps.map((step) => (
                          <Step key={step.key}>
                            <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.7rem' } }}>
                              {step.label}
                            </StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    </Box>

                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Tooltip title="Editar">
                        <IconButton
                          onClick={() => handleOpenDialog(asset)}
                          size="small"
                          sx={{ '&:hover': { backgroundColor: 'primary.main', color: 'white' } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Histórico">
                        <IconButton
                          onClick={() => handleShowHistory(asset)}
                          size="small"
                          sx={{ '&:hover': { backgroundColor: 'info.main', color: 'white' } }}
                        >
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {canManageInstallments && (
                        <Tooltip title="Parcelas da Aquisição: lançar a compra paga em mais de uma vez">
                          <IconButton
                            onClick={() => setInstallmentsAsset(asset)}
                            size="small"
                            sx={{ '&:hover': { backgroundColor: 'success.main', color: 'white' } }}
                          >
                            <PaymentsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="Adicionar Custo">
                        <IconButton
                          onClick={() => handleOpenPreparationCostDialog(asset)}
                          size="small"
                          sx={{ '&:hover': { backgroundColor: 'warning.main', color: 'white' } }}
                        >
                          <AddCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Ver Custos">
                        <IconButton
                          onClick={() => handleShowPreparationCosts(asset)}
                          size="small"
                          sx={{ '&:hover': { backgroundColor: 'secondary.main', color: 'white' } }}
                        >
                          <ListIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Retroceder">
                        <IconButton
                          onClick={() => handleRevertStatus(asset.id)}
                          size="small"
                          disabled={asset.status === 'em_aquisicao'}
                          sx={{ '&:hover': { backgroundColor: 'error.main', color: 'white' } }}
                        >
                          <ArrowBackIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Avançar">
                        <IconButton
                          onClick={() => handleAdvanceStatus(asset.id)}
                          size="small"
                          disabled={asset.status === 'entregue' || asset.status === 'cancelado'}
                          sx={{ '&:hover': { backgroundColor: 'success.main', color: 'white' } }}
                        >
                          <ArrowForwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Deletar">
                        <IconButton
                          onClick={() => handleDeleteAsset(asset.id)}
                          size="small"
                          sx={{ '&:hover': { backgroundColor: 'error.main', color: 'white' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Empty State - quando não há ativos */}
      {filteredAssets.length === 0 && !loading && (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.9), rgba(248,250,252,0.9))'
          }}
        >
          <InventoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all' ? 'Nenhum ativo encontrado' : 'Nenhum ativo cadastrado'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all'
              ? 'Tente ajustar os filtros para encontrar os ativos desejados.' 
              : 'Comece adicionando seu primeiro ativo ao estoque.'
            }
          </Typography>
          {(!searchTerm && statusFilter === 'all' && categoryFilter === 'all') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                boxShadow: '0 3px 10px rgba(25, 118, 210, 0.3)'
              }}
            >
              Adicionar Primeiro Ativo
            </Button>
          )}
        </Paper>
      )}
      </TabPanel>

      {/* Tab 2: Inventory Analytics */}
      <TabPanel value={currentTab} index={1}>
        <InventoryAnalytics 
          filters={{
            startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // Last 30 days
            endDate: new Date()
          }} 
        />
      </TabPanel>

      {/* Tab 3: Finance Analytics */}
      <TabPanel value={currentTab} index={2}>
        <FinanceAnalytics 
          filters={{
            startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // Last 30 days
            endDate: new Date()
          }} 
        />
      </TabPanel>

      {/* Dialog para criar/editar ativo */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedAsset ? 'Editar Ativo' : 'Novo Ativo'}
        </DialogTitle>
        <DialogContent dividers>
          {shouldCreateEditRequest && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Este ativo está travado porque a transação de aquisição foi verificada. Suas alterações serão enviadas como <strong>solicitação</strong> para aprovação do financeiro/admin.
              </Alert>
              <TextField
                fullWidth
                label="Motivo da solicitação (opcional)"
                value={editRequestReason}
                onChange={(e) => setEditRequestReason(e.target.value)}
                margin="dense"
                multiline
                rows={2}
              />
            </>
          )}
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome do Ativo"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                margin="dense"
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Categoria"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                margin="dense"
                required
                helperText="Preencha este campo."
              >
                <MenuItem value="">
                  <em>Selecione uma categoria</em>
                </MenuItem>
                {ASSET_CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Marca"
                value={formData.brand}
                onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                margin="dense"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Modelo"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                margin="dense"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Ano"
                value={formData.year}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  if (value.length <= 4) {
                    setFormData({ ...formData, year: value });
                  }
                }}
                margin="dense"
                inputProps={{ 
                  maxLength: 4,
                  pattern: '[0-9]*'
                }}
                placeholder="YYYY"
                helperText="Formato: YYYY (ex: 2024)"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Valor Inicial"
                type="number"
                value={formData.initial_value}
                onChange={(e) => setFormData({ ...formData, initial_value: e.target.value })}
                margin="dense"
                required
                inputProps={{ min: 0, step: 0.01 }}
                disabled={Boolean(selectedAsset)}
              />
              {!selectedAsset && canManageInstallments && (
                <FormControlLabel
                  control={<Switch checked={payInInstallments} onChange={(e) => setPayInInstallments(e.target.checked)} />}
                  label="Pagamento parcelado"
                />
              )}
              {!selectedAsset && canManageInstallments && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {payInInstallments
                    ? 'Informe em “Valor Inicial” o valor total da compra. Ao clicar em Criar, abre a tela para definir as parcelas e suas datas.'
                    : 'Marque se a compra será paga em mais de uma vez. Ativos já cadastrados podem ser parcelados pelo botão “Parcelas da Aquisição” na lista.'}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <AsyncSearchSelect
                label="Banco"
                value={formData.bank_id || ''}
                selectedLabel={selectedBankLabel}
                onChange={(id, option) => {
                  const nextId = id != null ? String(id).trim() : '';
                  setFormData({ ...formData, bank_id: nextId });
                  setSelectedBankLabel(option?.label || '');
                }}
                fetchPage={({ q, offset, limit }) => financeService.searchBanks({ q, offset, limit })}
                placeholder="Buscar banco..."
                textFieldProps={{ margin: 'dense' }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <AsyncSearchSelect
                label="Subclassificação (opcional)"
                value={formData.subclassification_id || ''}
                selectedLabel={selectedSubclassificationLabel}
                onChange={(id, option) => {
                  const nextId = id != null ? String(id).trim() : '';
                  setFormData({ ...formData, subclassification_id: nextId });
                  setSelectedSubclassificationLabel(option?.label || '');
                }}
                fetchPage={({ q, offset, limit }) =>
                  financeService.searchSubclassifications({
                    q,
                    offset,
                    limit,
                    classification: selectedAsset?.acquisition_classification || 'custo_variavel'
                  })
                }
                placeholder="Buscar subclassificação..."
                textFieldProps={{ margin: 'dense' }}
              />
            </Grid>

            {!selectedAsset && (
              <>
                <Grid item xs={12} md={4}>
                  <DateTimePicker
                    label="Data da Transação"
                    value={formData.transaction_date}
                    onChange={(value) => setFormData({ ...formData, transaction_date: value })}
                    slotProps={{ textField: { fullWidth: true, margin: 'dense' } }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <DateTimePicker
                    label="Data de Vencimento"
                    value={formData.due_date}
                    onChange={(value) => setFormData({ ...formData, due_date: value })}
                    slotProps={{ textField: { fullWidth: true, margin: 'dense' } }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <DateTimePicker
                    label="Data de Pagamento"
                    value={formData.payment_date}
                    onChange={(value) => setFormData({ ...formData, payment_date: value })}
                    slotProps={{ textField: { fullWidth: true, margin: 'dense' } }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Custos de Preparação"
                type="number"
                value={formData.preparation_costs}
                onChange={(e) => setFormData({ ...formData, preparation_costs: e.target.value })}
                margin="dense"
                inputProps={{ min: 0, step: 0.01 }}
                disabled
                helperText="Custos de preparação são adicionados pelo botão 'Adicionar Custo de Preparação'."
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Localização"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                margin="dense"
              />
            </Grid>
            
            <Grid item xs={12}>
              <AsyncSearchSelect
                label="ID do Produto/Sorteio"
                value={formData.product_id || ''}
                selectedLabel={selectedProductLabel}
                onChange={(id, option) => {
                  const nextId = id != null ? String(id).trim() : '';
                  setFormData({ ...formData, product_id: nextId });
                  setSelectedProductLabel(option?.label || '');
                }}
                fetchPage={({ q, offset, limit }) => financeService.searchProducts({ q, offset, limit })}
                placeholder="Buscar produto..."
                textFieldProps={{}}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                margin="dense"
                multiline
                rows={2}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observações"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                margin="dense"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Anexos
          </Typography>

          {!selectedAsset ? (
            <Alert severity="info">
              Salve o ativo para anexar arquivos.
            </Alert>
          ) : (
            <>
              <Stack direction={isMobile ? 'column' : 'row'} spacing={1} sx={{ mb: 1, alignItems: isMobile ? 'stretch' : 'center' }}>
                {canCreateAssetAttachments && (
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<AttachFileIcon />}
                    disabled={assetAttachmentsUploading}
                  >
                    {assetAttachmentsUploading ? 'Enviando...' : 'Adicionar arquivo'}
                    <input type="file" hidden multiple onChange={handleUploadAssetFiles} />
                  </Button>
                )}
                {canReadAssetAttachments && (
                  <Button
                    variant="text"
                    onClick={() => loadAssetAttachments(selectedAsset.id)}
                    disabled={assetAttachmentsLoading}
                  >
                    Atualizar
                  </Button>
                )}
              </Stack>

              {(assetAttachmentsLoading || assetAttachmentsUploading) && (
                <LinearProgress sx={{ mb: 1 }} />
              )}

              {!canReadAssetAttachments ? (
                <Alert severity="warning">Você não tem permissão para ver anexos.</Alert>
              ) : assetAttachments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum anexo.
                </Typography>
              ) : (
                <List dense>
                  {assetAttachments.map((a) => (
                    <ListItem
                      key={a.id}
                      divider
                      secondaryAction={
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Visualizar">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenAssetAttachment(a.id, 'view')}
                                disabled={!canReadAssetAttachments}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Download">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenAssetAttachment(a.id, 'download')}
                                disabled={!canReadAssetAttachments}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {canDeleteAssetAttachments && (
                            <Tooltip title="Remover">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteAssetAttachment(a.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      }
                    >
                      <ListItemText
                        primary={a.original_filename}
                        secondary={`${formatBytes(a.size_bytes)}${a.content_type ? ` • ${a.content_type}` : ''}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancelar
          </Button>
          <Button 
            onClick={() => runSaveAsset(handleSaveAsset)}
            variant="contained"
            disabled={savingAsset || (selectedAsset ? (!formData.name || !formData.category) : (!formData.name || !formData.category || !formData.initial_value))}
          >
            {selectedAsset ? (shouldCreateEditRequest ? 'Enviar para aprovação' : 'Atualizar') : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para histórico de status */}
      <Dialog 
        open={historyDialogOpen} 
        onClose={() => setHistoryDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Histórico de Status - {selectedAsset?.name}
        </DialogTitle>
        <DialogContent dividers>
          <List>
            {assetHistory.map((item, index) => (
              <ListItem key={item.id} divider={index < assetHistory.length - 1}>
                <ListItemText
                  primary={
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">
                        {item.previous_status ? `${getStatusLabel(item.previous_status)} →` : 'Criado:'} {' '}
                        <strong>{getStatusLabel(item.new_status)}</strong>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(item.created_at).toLocaleString('pt-BR')}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {item.reason}
                      </Typography>
                      {item.notes && (
                        <Typography variant="caption" color="text.secondary">
                          Obs: {item.notes}
                        </Typography>
                      )}
                      <Typography variant="caption" display="block">
                        por {item.changer_name || 'Sistema'}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Confirmação para Ações de Status */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
            Cancelar
          </Button>
          <Button onClick={confirmStatusAction} color="primary" autoFocus>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de relação dos custos adicionais */}
      <Dialog open={preparationCostsDialogOpen} onClose={() => setPreparationCostsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Custos de Preparação</DialogTitle>
        <DialogContent>
          {preparationCostsList.length === 0 ? (
            <Typography color="textSecondary">Nenhum custo de preparação registrado para este ativo.</Typography>
          ) : (
            <List>
              {preparationCostsList.map((cost) => (
                <ListItem key={cost.id} divider>
                  <ListItemText
                    primary={formatCurrency(cost.amount) + ' - ' + cost.description}
                    secondary={
                      <Box>
                        {cost.notes && <Typography variant="body2" color="textSecondary">{cost.notes}</Typography>}
                        <Typography variant="caption" color="textSecondary">
                          {new Date(cost.transaction_date).toLocaleDateString('pt-BR')}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreparationCostsDialogOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para Adicionar Custo de Preparação */}
      <Dialog open={preparationCostDialog} onClose={() => setPreparationCostDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar Custo de Preparação</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Valor"
                  type="number"
                  value={preparationCostData.amount}
                  onChange={(e) => setPreparationCostData({ ...preparationCostData, amount: e.target.value })}
                  margin="dense"
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>

              <Grid item xs={12}>
                <DateTimePicker
                  label="Data da Transação"
                  value={preparationCostData.transaction_date}
                  onChange={(value) => setPreparationCostData({ ...preparationCostData, transaction_date: value })}
                  slotProps={{ textField: { fullWidth: true, margin: 'dense' } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Data de Vencimento"
                  value={preparationCostData.due_date}
                  onChange={(value) => setPreparationCostData({ ...preparationCostData, due_date: value })}
                  slotProps={{ textField: { fullWidth: true, margin: 'dense' } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Data de Pagamento"
                  value={preparationCostData.payment_date}
                  onChange={(value) => setPreparationCostData({ ...preparationCostData, payment_date: value })}
                  slotProps={{ textField: { fullWidth: true, margin: 'dense' } }}
                />
              </Grid>

              <Grid item xs={12}>
                <AsyncSearchSelect
                  label="Banco"
                  value={preparationCostData.bank_id || ''}
                  selectedLabel={preparationCostBankLabel}
                  onChange={(id, option) => {
                    const nextId = id != null ? String(id).trim() : '';
                    setPreparationCostData({ ...preparationCostData, bank_id: nextId });
                    setPreparationCostBankLabel(option?.label || '');
                  }}
                  fetchPage={({ q, offset, limit }) => financeService.searchBanks({ q, offset, limit })}
                  placeholder="Buscar banco..."
                  textFieldProps={{ margin: 'dense' }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Classificação"
                  value={preparationCostData.classification || 'custo_variavel'}
                  onChange={(e) => {
                    const next = e.target.value;
                    setPreparationCostData({
                      ...preparationCostData,
                      classification: next,
                      subclassification_id: ''
                    });
                    setPreparationCostSubclassificationLabel('');
                  }}
                  margin="dense"
                  required
                  helperText="Defina como este custo entra na DRE."
                >
                  {(
                    enums.transactionClassifications?.length
                      ? enums.transactionClassifications
                      : [{ value: 'custo_variavel', label: 'Custo Variavel' }]
                  ).map((classification) => (
                    <MenuItem key={classification.value} value={classification.value}>
                      {classification.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <AsyncSearchSelect
                  label="Subclassificação (opcional)"
                  value={preparationCostData.subclassification_id || ''}
                  selectedLabel={preparationCostSubclassificationLabel}
                  onChange={(id, option) => {
                    const nextId = id != null ? String(id).trim() : '';
                    setPreparationCostData({ ...preparationCostData, subclassification_id: nextId });
                    setPreparationCostSubclassificationLabel(option?.label || '');
                  }}
                  fetchPage={({ q, offset, limit }) =>
                    financeService.searchSubclassifications({
                      q,
                      offset,
                      limit,
                      classification: preparationCostData.classification || 'custo_variavel'
                    })
                  }
                  placeholder="Buscar subclassificação..."
                  textFieldProps={{ margin: 'dense' }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descrição"
                  value={preparationCostData.description}
                  onChange={(e) => setPreparationCostData({ ...preparationCostData, description: e.target.value })}
                  margin="dense"
                  required
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Observações"
                  value={preparationCostData.notes}
                  onChange={(e) => setPreparationCostData({ ...preparationCostData, notes: e.target.value })}
                  margin="dense"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreparationCostDialog(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={() => runSaveCost(handleAddPreparationCost)}
            color="primary"
            disabled={savingCost || !preparationCostData.amount || !preparationCostData.description}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      <AssetInstallmentsDialog
        asset={installmentsAsset}
        onClose={() => setInstallmentsAsset(null)}
        onSaved={() => {
          setInstallmentsAsset(null);
          showNotification('Parcelas salvas com sucesso', 'success');
          invalidateCache('asset_summary_dashboard');
          loadData();
        }}
      />
    </Container>
  );
};

export default AssetManagement;